import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toast } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './PatientList.css';
import './DoctorInformation.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_API_URL;
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const DAY_LABELS = {
  Monday: 'Monday',
  Tuesday: 'Tuesday',
  Wednesday: 'Wednesday',
  Thursday: 'Thursday',
  Friday: 'Friday',
  Saturday: 'Saturday',
  Sunday: 'Sunday'
};
// Hour list options (00:00, 00:30, ..., 23:30)
const HOUR_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? '00' : '30';
  return `${String(h).padStart(2, '0')}:${m}`;
});
// Normalize backend time values to HH:MM (drop seconds, pad hour/min)
const toHHMM = (val) => {
  if (!val) return '';
  // e.g., "9:00", "09:00:00", "09:30" -> keep HH:MM
  const parts = String(val).split(':');
  const h = parts[0] ? String(parseInt(parts[0], 10)).padStart(2, '0') : '00';
  const m = parts[1] ? String(parseInt(parts[1], 10)).padStart(2, '0') : '00';
  return `${h}:${m}`;
};
// Convert HH:MM (24h) to 12-hour with AM/PM for display
const to12h = (val) => {
  const hhmm = toHHMM(val);
  if (!hhmm) return '';
  const [hStr, mStr] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mStr} ${ampm}`;
};

const DoctorInformation = () => {
  const [doctor, setDoctor] = useState({
    full_name: '',
    gender: 'male',
    email: '',
    phone_number: '',
    field_id: 1, // Default field ID, adjust as needed
    brief: '',
    license_number: '',
    license_date: '',
    consultation_fee: 0,
    clinic_consultation_fee: 0,
    is_phone: true,
    is_clinic: true,
    clinic_address: '',
    info: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    snapchat_url: '',
    telegram_url: '',
    per_30min_capacity: 4,
    booking_mode: 'day',
    username: '',
    open_days: [],
    open_day_times: {}
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });
  const [clinicLocation, setClinicLocation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [copyingQR, setCopyingQR] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const actionsBtnRef = useRef(null);
  const actionsMenuRef = useRef(null);
  const actionsContainerRef = useRef(null);
  // Closed dates (clinic overrides)
  const [closedDates, setClosedDates] = useState([]); // [{id, date}]
  const [newClosedDate, setNewClosedDate] = useState(''); // YYYY-MM-DD
  // Delete account modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const showMessage = (message, error = false) => {
    setToastMessage(message);
    setIsError(error);
    setShowToast(true);
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.new_password_confirmation) {
      showMessage('Please fill in all password fields', true);
      return;
    }

    if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
      showMessage('New password confirmation does not match', true);
      return;
    }

    try {
            
      await axios.post(
        `${API_URL}doctor/change-password`,
        {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
          new_password_confirmation: passwordForm.new_password_confirmation
        },
        {
          headers: {
            'Accept': 'application/json'
          }
        }
      );

      showMessage('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', new_password_confirmation: '' });
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to change password';
      showMessage(msg, true);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      showMessage('Please enter password to confirm', true);
      return;
    }

    setDeleting(true);

    try {
      await axios.post(
        `${API_URL}doctor/delete-account`,
        { password: deletePassword },
        {
          headers: {
            'Accept': 'application/json'
          }
        }
      );

      showMessage('Account deleted successfully');
      
      // Clear local storage and redirect to login
      localStorage.removeItem('userInfo');
      localStorage.removeItem('accountSessions');
      localStorage.removeItem('activeAccountId');
      
      setTimeout(() => {
        navigate('/control/login');
      }, 1500);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to delete account';
      showMessage(msg, true);
      setDeleting(false);
    }
  };

  // Close dropdown on outside click (and touch) when open
  useEffect(() => {
    if (!showActionsMenu) return;
    const handleDocClick = (e) => {
      const menuEl = actionsMenuRef.current;
      const btnEl = actionsBtnRef.current;
      if (menuEl && menuEl.contains(e.target)) return;
      if (btnEl && btnEl.contains(e.target)) return;
      setShowActionsMenu(false);
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('touchstart', handleDocClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('touchstart', handleDocClick);
    };
  }, [showActionsMenu]);

  // Decide dropdown alignment to avoid viewport cutoff when opening
  useEffect(() => {
    if (!showActionsMenu) return;
    try {
      const container = actionsContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const estimatedWidth = 280; // px, matches minWidth + padding
      const margin = 8; // safe margin to viewport edge
      if (rect.left + estimatedWidth > window.innerWidth - margin) {
        //setMenuAlignRight(true); // align to right if it would overflow on left
      } else {
        //setMenuAlignRight(false); // align to left by default
      }
    } catch (_) {}
  }, [showActionsMenu]);

  useEffect(() => {
    const getCurrentLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setCurrentLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
            setLocationLoading(false);
            //setMapKey(prev => prev + 1);
          },
          (error) => {
            console.log('Geolocation error:', error);
            setLocationLoading(false);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5 minutes
          }
        );
      } else {
        setLocationLoading(false);
      }
    };

    const fetchDoctorInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}doctor/get-doctor`, {
          headers: { 'Accept': 'application/json' }
        });
        
        const data = response.data.data || response.data;
        // Normalize open_days from API: it may be an array of strings (old) or array of objects (new)
        let openDaysList = [];
        let timesMap = {};
        if (Array.isArray(data.open_days)) {
          if (data.open_days.length && typeof data.open_days[0] === 'object') {
            // New format: [{ day_name, opening_time, closing_time }, ...]
            openDaysList = data.open_days.map(d => d.day_name).filter(Boolean);
            timesMap = data.open_days.reduce((acc, d) => {
              if (d && d.day_name) {
                acc[d.day_name] = {
                  opening_time: toHHMM(d.opening_time || ''),
                  closing_time: toHHMM(d.closing_time || '')
                };
              }
              return acc;
            }, {});
          } else {
            // Old format: ["Monday", "Tuesday", ...]
            openDaysList = data.open_days;
            const legacy = data.open_day_times || {};
            timesMap = Object.keys(legacy).reduce((acc, k) => {
              acc[k] = {
                opening_time: toHHMM(legacy[k]?.opening_time || ''),
                closing_time: toHHMM(legacy[k]?.closing_time || ''),
              };
              return acc;
            }, {});
          }
        }

        setDoctor({
          full_name: data.full_name || '',
          gender: data.gender || 'male',
          email: data.email || '',
          phone_number: data.phone_number || '',
          brief: data.brief || '',
          license_number: data.license_number || '',
          license_date: data.license_date || '',
          clinic_address: data.clinic_address || '',
          consultation_fee: data.consultation_fee ?? 0,
          clinic_consultation_fee: data.clinic_consultation_fee ?? 0,
          is_clinic: data.is_clinic ?? false,
          facebook_url: data.facebook_url || '',
          instagram_url: data.instagram_url || '',
          tiktok_url: data.tiktok_url || '',
          snapchat_url: data.snapchat_url || '',
          telegram_url: data.telegram_url || '',
          per_30min_capacity: (typeof data.per_30min_capacity !== 'undefined' && data.per_30min_capacity !== null) ? data.per_30min_capacity : 4,
          booking_mode: data.booking_mode || 'day',
          username: data.username || '',
          open_days: openDaysList,
          open_day_times: timesMap
        });
        // Initialize closed dates if provided, then refresh from API for consistency
        if (Array.isArray(data.closed_dates)) {
          setClosedDates((data.closed_dates || []).map(d => ({ id: null, date: d })));
        }
        await loadClosedDates();
        
        // Load saved clinic location if available, otherwise get current location for display
        if (data.clinic_latitude && data.clinic_longitude) {
          setClinicLocation({
            latitude: parseFloat(data.clinic_latitude),
            longitude: parseFloat(data.clinic_longitude)
          });
          setLocationLoading(false);
        } else {
          getCurrentLocation();
        }
      } catch (error) {
        console.error('Error fetching doctor information:', error);
        showMessage('Failed to load doctor information', true);
        setLocationLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctorInfo();
  }, [navigate]);

  // Load closed dates list for authenticated doctor
  const loadClosedDates = async () => {
    try {
                  const res = await axios.get(`${API_URL}doctor/closed-dates`, {
        headers: { 'Accept': 'application/json' }
      });
      const payload = res.data.data || [];
      // payload expected: [{id, date}]
      setClosedDates(Array.isArray(payload) ? payload : []);
    } catch (err) {
      console.error('Failed to load closed dates', err);
    }
  };

  const handleAddClosedDate = async (e) => {
    e?.preventDefault?.();
    if (!newClosedDate) {
      showMessage('Please select a date to add', true);
      return;
    }
    try {
            await axios.post(`${API_URL}doctor/closed-dates`, { date: newClosedDate }, {
        headers: { 'Accept': 'application/json' }
      });
      setNewClosedDate('');
      await loadClosedDates();
      showMessage('Closed date added successfully');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to add closed date';
      showMessage(msg, true);
    }
  };

  const handleDeleteClosedDate = async (id) => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this date?')) return;
    try {
            await axios.delete(`${API_URL}doctor/closed-dates/${id}`, {
        headers: { 'Accept': 'application/json' }
      });
      await loadClosedDates();
      showMessage('Closed date deleted');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to delete closed date';
      showMessage(msg, true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDoctor(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleOpenDay = (day) => {
    setDoctor(prev => {
      const exists = prev.open_days?.includes(day);
      const next = exists
        ? prev.open_days.filter(d => d !== day)
        : [...(prev.open_days || []), day];
      // maintain times: remove times when day is unchecked
      const nextTimes = { ...(prev.open_day_times || {}) };
      if (!exists) {
        // ensure time object exists when adding
        if (!nextTimes[day]) nextTimes[day] = { opening_time: '', closing_time: '' };
      } else {
        delete nextTimes[day];
      }
      return { ...prev, open_days: next, open_day_times: nextTimes };
    });
  };

  const selectAllOpenDays = () => {
    setDoctor(prev => ({ ...prev, open_days: [...DAYS] }));
  };

  const clearOpenDays = () => {
    setDoctor(prev => ({ ...prev, open_days: [], open_day_times: {} }));
  };

  const handleTimeChange = (day, field, value) => {
    setDoctor(prev => {
      const times = { ...(prev.open_day_times || {}) };
      if (!times[day]) times[day] = { opening_time: '', closing_time: '' };
      times[day] = { ...times[day], [field]: value };
      return { ...prev, open_day_times: times };
    });
  };

  // OpenStreetMap component with click handler
  const MapClickHandler = ({ onMapClick }) => {
    useMapEvents({
      click: (e) => {
        const { lat, lng } = e.latlng;
        onMapClick({ latitude: lat, longitude: lng });
      },
    });
    return null;
  };

  const OpenStreetMapComponent = ({ center, clinicLocation, onMapClick }) => {
    const mapCenter = clinicLocation 
      ? [clinicLocation.latitude, clinicLocation.longitude]
      : [center.lat, center.lng];

    return (
      <MapContainer
        center={mapCenter}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapClickHandler onMapClick={onMapClick} />
        {clinicLocation && (
          <Marker 
            position={[clinicLocation.latitude, clinicLocation.longitude]}
            title="Clinic Location"
          />
        )}
      </MapContainer>
    );
  };

  const resetToCurrentLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setClinicLocation(newLocation);
          setCurrentLocation(newLocation);
          setLocationLoading(false);
          //setMapKey(prev => prev + 1);
          alert('Current location set as clinic location');
        },
        (error) => {
          console.log('Geolocation error:', error);
          setLocationLoading(false);
          alert('Cannot get current location');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0 // Force fresh location
        }
      );
    } else {
      setLocationLoading(false);
      alert('Browser does not support geolocation');
    }
  };

  const clearLocation = () => {
    setClinicLocation(null);
    //setMapKey(prev => prev + 1);
    alert('Clinic location removed');
  };

  // Export patients CSV
  const handleExportPatients = async () => {
    try {
                  const res = await axios.get(`${API_URL}doctor/export-patients`, {
        headers: { 'Accept': 'application/json' },
        responseType: 'blob'
      });

      // Determine filename from Content-Disposition or fallback
      let filename = `patients_export_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.zip`;
      const dispo = res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition']);
      if (dispo) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(dispo);
        const name = decodeURIComponent(match?.[1] || match?.[2] || '');
        if (name) filename = name;
      }

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      showMessage('CSV file downloaded successfully');
    } catch (err) {
      console.error('Failed to export patients', err);
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to download patients file';
      showMessage(msg, true);
    }
  };

  const handleClearCache = () => {
    if (window.confirm('Do you want to refresh the page and clear cache?')) {
      // Clear browser cache and reload
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
          });
        });
      }
      // Force hard reload
      window.location.reload(true);
    }
  };

  const copyProfileURL = async () => {
    try {
      setCopyingQR(true);
      
      const profileURL = `${window.location.origin}/doctor/${doctor.username}`;
      
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(profileURL);
          showMessage('Profile link copied successfully');
          return;
        } catch (clipboardError) {
          console.warn('Clipboard API failed, trying fallback:', clipboardError);
        }
      }
      
      // Fallback method for Electron and older browsers
      const textArea = document.createElement('textarea');
      textArea.value = profileURL;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          showMessage('Profile link copied successfully');
        } else {
          throw new Error('execCommand failed');
        }
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        showMessage('Failed to copy link. Please copy the link manually', true);
      } finally {
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Error copying profile URL:', error);
      showMessage('Failed to copy link', true);
    } finally {
      setCopyingQR(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doctor.full_name.trim()) {
      showMessage('Full name is required', true);
      return;
    }

    try {
      setSaving(true);
            
      const formData = new FormData();
      
      // Add scalar fields to formData (skip arrays like open_days)
      Object.keys(doctor).forEach(key => {
        if (key === 'open_days') return;
        if (doctor[key] !== null && doctor[key] !== undefined) {
          formData.append(key, doctor[key]);
        }
      });

      // Add open_days as array items for Laravel (open_days[])
      if (Array.isArray(doctor.open_days)) {
        doctor.open_days.forEach(d => formData.append('open_days[]', d));
      }

      // Add open_day_times as nested fields: open_day_times[Day][opening_time]
      if (doctor.open_day_times && typeof doctor.open_day_times === 'object') {
        DAYS.forEach((day) => {
          if (doctor.open_days.includes(day) && doctor.open_day_times[day]) {
            const ot = doctor.open_day_times[day].opening_time || '';
            const ct = doctor.open_day_times[day].closing_time || '';
            if (ot !== '') formData.append(`open_day_times[${day}][opening_time]`, ot);
            if (ct !== '') formData.append(`open_day_times[${day}][closing_time]`, ct);
          }
        });
      }
      
      // Add clinic location coordinates (send null if cleared)
      if (clinicLocation) {
        formData.append('clinic_latitude', clinicLocation.latitude);
        formData.append('clinic_longitude', clinicLocation.longitude);
      } else {
        formData.append('clinic_latitude', '');
        formData.append('clinic_longitude', '');
      }
      
      // Ensure required fields have default values
      if (!formData.has('is_phone')) formData.append('is_phone', true);
      
      await axios.post(
        `${API_URL}doctor/update-doctor`,
        formData,
        {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json'
          }
        }
      );
      
      showMessage('Profile updated successfully');
    } catch (error) {
      console.error('Error updating doctor information:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      showMessage(errorMessage, true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-list-container" dir="ltr">
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <Toast 
          onClose={() => setShowToast(false)} 
          show={showToast} 
          delay={3000} 
          autohide
          className={isError ? 'bg-danger text-white' : 'bg-success text-white'}
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </div>
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-10">
            <div className="card patient-list-card">
              <div className="card-header patient-list-header d-flex justify-content-between align-items-center">
                <div className="w-100 d-flex justify-content-between align-items-center flex-nowrap" dir="ltr">
                  <div className="d-flex align-items-center">
                    <h4>Profile Information</h4>
                  </div>
                  {/* Actions dropdown removed */}
                </div>
              </div>
              <div className="card-body patient-list-body text-start">
                <form onSubmit={handleSubmit}>
                  {/* Tabs Header */}
                  <div className="mb-4 w-100">
                  <ul className="nav nav-pills tabs-underline w-100 d-flex justify-content-start" dir="ltr">
                    <li className="nav-item flex-fill">
                      <button type="button" className={`nav-link w-100 ${activeTab === 'personal' ? 'active' : ''}`} onClick={() => setActiveTab('personal')}>
                        <i className="fas fa-user ms-2"></i><span className="d-none d-md-inline"> Personal</span>
                      </button>
                    </li>
                    <li className="nav-item flex-fill">
                      <button type="button" className={`nav-link w-100 ${activeTab === 'professional' ? 'active' : ''}`} onClick={() => setActiveTab('professional')}>
                        <i className="fas fa-certificate ms-2"></i><span className="d-none d-md-inline"> Professional</span>
                      </button>
                    </li>
                    <li className="nav-item flex-fill">
                      <button type="button" className={`nav-link w-100 ${activeTab === 'clinic' ? 'active' : ''}`} onClick={() => setActiveTab('clinic')}>
                        <i className="fas fa-clinic-medical ms-2"></i><span className="d-none d-md-inline"> Clinic</span>
                      </button>
                    </li>
                    <li className="nav-item flex-fill">
                      <button type="button" className={`nav-link w-100 ${activeTab === 'social' ? 'active' : ''}`} onClick={() => setActiveTab('social')}>
                        <i className="fas fa-share-alt ms-2"></i><span className="d-none d-md-inline"> Social</span>
                      </button>
                    </li>
                    {/* Profile tab removed and merged into Social tab */}
                  </ul>
                  </div>

                  {/* Tabs Content */}
                  {activeTab === 'personal' && (
                    <div className="search-filters-section mb-4">
                      <h5 className="mb-3 text-start">
                        <i className="fas fa-user me-2"></i>
                        Personal Information
                      </h5>
                      <div className="mb-3">
                        <label htmlFor="full_name" className="form-label text-start d-block">Full Name * (in Arabic)</label>
                        <input
                          type="text"
                          className="form-control text-start"
                          id="full_name"
                          name="full_name"
                          value={doctor.full_name}
                          onChange={handleInputChange}
                          required
                          dir="ltr"
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="brief" className="form-label text-start d-block">About the Doctor</label>
                        <textarea
                          className="form-control text-start"
                          id="brief"
                          name="brief"
                          rows="3"
                          value={doctor.brief || ''}
                          onChange={handleInputChange}
                          placeholder="Please write about the doctor"
                          dir="ltr"
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="phone_number" className="form-label text-start d-block">Phone Number</label>
                        <input
                          type="tel"
                          className="form-control text-start"
                          id="phone_number"
                          name="phone_number"
                          value={doctor.phone_number}
                          onChange={handleInputChange}
                          dir="ltr"
                        />
                      </div>

                      <div className="mb-3 mt-4 p-3 border rounded bg-white">
                        <h6 className="mb-3 text-start">
                          <i className="fas fa-lock me-2"></i>
                          Change Password
                        </h6>
                        <div className="row g-3">
                          <div className="col-md-4">
                            <label htmlFor="current_password" className="form-label text-start d-block">Current Password</label>
                            <input
                              type="password"
                              className="form-control text-start"
                              id="current_password"
                              name="current_password"
                              value={passwordForm.current_password}
                              onChange={handlePasswordInputChange}
                              dir="ltr"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="new_password" className="form-label text-start d-block">New Password</label>
                            <input
                              type="password"
                              className="form-control text-start"
                              id="new_password"
                              name="new_password"
                              value={passwordForm.new_password}
                              onChange={handlePasswordInputChange}
                              dir="ltr"
                            />
                          </div>
                          <div className="col-md-4">
                            <label htmlFor="new_password_confirmation" className="form-label text-start d-block">Confirm New Password</label>
                            <input
                              type="password"
                              className="form-control text-start"
                              id="new_password_confirmation"
                              name="new_password_confirmation"
                              value={passwordForm.new_password_confirmation}
                              onChange={handlePasswordInputChange}
                              dir="ltr"
                            />
                          </div>
                        </div>
                        <div className="mt-3 d-flex justify-content-start">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={handleChangePassword}
                          >
                            <i className="fas fa-key ms-2"></i>
                            Update Password
                          </button>
                        </div>
                      </div>

                      <div className="mb-3 mt-4 p-3 border rounded bg-light border-info ios-app">
                        <h6 className="mb-3 text-start text-info">
                          <i className="fas fa-download me-2"></i>
                          Data Backup
                        </h6>
                        <p className="text-start text-muted mb-3">
                          Download a complete backup of your data including patient information, notes, prescriptions, and referrals.
                        </p>
                        <div className="d-flex justify-content-start">
                          <button
                            type="button"
                            className="btn btn-info"
                            onClick={handleExportPatients}
                          >
                            <i className="fas fa-file-archive ms-2"></i>
                            Download Backup
                          </button>
                        </div>
                      </div>

                      <div className="mb-3 mt-4 p-3 border rounded bg-light border-secondary">
                        <h6 className="mb-3 text-start text-secondary">
                          <i className="fas fa-sync-alt me-2"></i>
                          Refresh Page
                        </h6>
                        <p className="text-start text-muted mb-3">
                          Refresh the page to see the latest updates.
                        </p>
                        <div className="d-flex justify-content-start">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClearCache}
                          >
                            <i className="fas fa-sync-alt ms-2"></i>
                            Refresh & Clear Cache
                          </button>
                        </div>
                      </div>

                      <div className="mb-3 mt-4 p-3 border rounded bg-light border-danger">
                        <h6 className="mb-3 text-start text-danger">
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          Delete Account
                        </h6>
                        <p className="text-start text-muted mb-3">
                          Warning: Deleting your account will permanently delete all your data and cannot be undone.
                        </p>
                        <div className="d-flex justify-content-start">
                          <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => setShowDeleteModal(true)}
                          >
                            <i className="fas fa-trash-alt ms-2"></i>
                            Delete Account Permanently
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'professional' && (
                    <div className="search-filters-section mb-4">
                      <h5 className="mb-3 text-start">
                        <i className="fas fa-certificate me-2"></i>
                        Professional Information
                      </h5>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="license_number" className="form-label text-start d-block">License Number</label>
                          <input
                            type="text"
                            className="form-control text-start"
                            id="license_number"
                            name="license_number"
                            value={doctor.license_number || ''}
                            onChange={handleInputChange}
                            placeholder="Enter license number"
                            dir="ltr"
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="license_date" className="form-label text-start d-block">License Date</label>
                          <input
                            type="date"
                            className="form-control text-start"
                            id="license_date"
                            name="license_date"
                            value={doctor.license_date || ''}
                            onChange={handleInputChange}
                            dir="ltr"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'clinic' && (
                    <div className="search-filters-section mb-4">
                      <h5 className="mb-4 text-start">
                        <i className="fas fa-clinic-medical me-2"></i>
                        Clinic Information
                      </h5>

                      {/* Section: Address & Map */}
                      <div className="mb-4 p-3 border rounded bg-white">
                        <h6 className="mb-3 text-start">
                          <i className="fas fa-map-marker-alt me-2"></i>
                          Address and Location
                        </h6>
                        <div className="mb-3">
                          <label htmlFor="clinic_address" className="form-label text-start d-block">Clinic Address</label>
                          <textarea
                            className="form-control text-start"
                            id="clinic_address"
                            name="clinic_address"
                            rows="3"
                            value={doctor.clinic_address || ''}
                            onChange={handleInputChange}
                            placeholder="Enter clinic address"
                            dir="ltr"
                          ></textarea>
                        </div>
                        <div>
                          <label className="form-label text-start d-block mb-3">Clinic Location on Map</label>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={resetToCurrentLocation}
                                disabled={locationLoading}
                              >
                                {locationLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Detecting...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-crosshairs me-1"></i>
                                    Use Current Location
                                  </>
                                )}
                              </button>
                              {clinicLocation && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={clearLocation}
                                >
                                  <i className="fas fa-times me-1"></i>
                                  Clear Location
                                </button>
                              )}
                            </div>
                            <small className="text-muted text-start">Click on the map to select clinic location</small>
                          </div>
                          <div style={{ height: '300px', border: '2px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
                            {(clinicLocation || currentLocation) ? (
                              <OpenStreetMapComponent
                                center={clinicLocation ? { lat: clinicLocation.latitude, lng: clinicLocation.longitude } : { lat: currentLocation.latitude, lng: currentLocation.longitude }}
                                clinicLocation={clinicLocation}
                                onMapClick={(location) => {
                                  setClinicLocation(location);
                                  //setMapKey(prev => prev + 1);
                                }}
                              />
                            ) : (
                              <div className="d-flex align-items-center justify-content-center h-100 bg-light">
                                <div className="text-center text-muted">
                                  <i className="fas fa-map-marker-alt fa-3x mb-3"></i>
                                  <p>Clinic location not set yet</p>
                                  <p>Use "Use Current Location" button or click on the map to set location</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="mt-2 text-start">
                            <small className="text-muted">
                              {clinicLocation ? (
                                <>Current Location: {clinicLocation.latitude.toFixed(6)}, {clinicLocation.longitude.toFixed(6)}</>
                              ) : (
                                'No location set'
                              )}
                            </small>
                          </div>
                        </div>
                      </div>

                      {/* Section: Fees & Online Booking */}
                      <div className="mb-4 p-3 border rounded bg-white">
                        <h6 className="mb-3 text-start">
                          <i className="fas fa-money-bill-wave me-2"></i>
                          Fees and Online Booking
                        </h6>
                        <div className="mb-3">
                          <label htmlFor="clinic_consultation_fee" className="form-label text-start d-block">Clinic Consultation Fee (Dinar)</label>
                          <input
                            type="number"
                            className="form-control text-start"
                            id="clinic_consultation_fee"
                            name="clinic_consultation_fee"
                            value={doctor.clinic_consultation_fee ?? 0}
                            onChange={handleInputChange}
                            dir="ltr"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="per_30min_capacity" className="form-label text-start d-block">Capacity per 30 Minutes</label>
                          <input
                            type="number"
                            className="form-control text-start"
                            id="per_30min_capacity"
                            name="per_30min_capacity"
                            value={doctor.per_30min_capacity ?? 4}
                            onChange={handleInputChange}
                            dir="ltr"
                            min="0"
                            step="1"
                            placeholder="4"
                          />
                        </div>
                        <small className="form-text text-muted d-block text-start">Sets the maximum number of patients that can be booked per 30 minutes.</small>
                        <div className="mb-3">
                          <label htmlFor="booking_mode" className="form-label text-start d-block">Online Booking Mode</label>
                          <select
                            id="booking_mode"
                            name="booking_mode"
                            className="form-select text-start"
                            value={doctor.booking_mode || 'day'}
                            onChange={handleInputChange}
                            dir="ltr"
                          >
                            <option value="time">By Time (Time Slots)</option>
                            <option value="day">By Day (Full Day)</option>
                          </select>
                          <div className="form-text text-start">
                            Choose "By Time" to allow patients to book within time slots during the day, or "By Day" to book the entire day without specifying time.
                          </div>
                        </div>
                        <div className="form-check form-switch mb-1 text-start">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="is_clinic"
                            name="is_clinic"
                            checked={!!doctor.is_clinic}
                            onChange={handleInputChange}
                          />
                          <label className="form-check-label ms-2" htmlFor="is_clinic">Enable Online Booking</label>
                        </div>
                        <div className="form-text text-start">When enabled, patients will be able to select an available day and send a booking request from your public page.</div>
                      </div>
                      

                      {/* Section: Open Days */}
                      <div className="mb-4 p-3 border rounded bg-white">
                        <h6 className="mb-3 text-start">
                          <i className="far fa-calendar-check me-2"></i>
                          Working Days
                        </h6>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                            <label className="form-label mb-2">Clinic Working Days</label>
                            <div className="d-flex gap-2">
                              <button type="button" className="btn btn-light btn-sm" onClick={selectAllOpenDays}>Select All</button>
                              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearOpenDays}>Clear</button>
                            </div>
                          </div>
                          <div className="open-days-group text-end">
                            {DAYS.map((day) => (
                              <span key={day} className="me-1 mb-2 d-inline-block">
                                <input
                                  type="checkbox"
                                  className="btn-check"
                                  id={`open_day_${day}`}
                                  autoComplete="off"
                                  checked={doctor.open_days?.includes(day) || false}
                                  onChange={() => toggleOpenDay(day)}
                                />
                                <label className="btn btn-outline-primary btn-sm rounded-pill" htmlFor={`open_day_${day}`}>
                                  <i className="far fa-calendar-check ms-1"></i>
                                  {DAY_LABELS[day] || day}
                                </label>
                              </span>
                            ))}
                          </div>
                          <small className="text-muted d-block text-start mt-1">Select the days the clinic is open.</small>
                        </div>

                        {doctor.open_days?.length > 0 && (
                          <div className="mb-0">
                            <label className="form-label text-start d-block">Working Hours for Each Day</label>
                            <div className="row g-2">
                              {doctor.open_days.map((day) => (
                                <div className="col-12" key={`times-${day}`}>
                                  <div className="p-2 border rounded d-flex flex-wrap align-items-center justify-content-between" dir="rtl">
                                    <strong className="mb-2 mb-sm-0">{DAY_LABELS[day] || day}</strong>
                                    <div className="d-flex gap-2 align-items-center flex-row" dir="ltr">
                                      <div className="input-group input-group-sm">
                                        <span className="input-group-text"><i className="far fa-clock"></i></span>
                                        <select
                                          className="form-select"
                                          value={doctor.open_day_times?.[day]?.closing_time || ''}
                                          onChange={(e) => handleTimeChange(day, 'closing_time', e.target.value)}
                                        >
                                          <option value="">—</option>
                                          {doctor.open_day_times?.[day]?.closing_time && !HOUR_OPTIONS.includes(doctor.open_day_times[day].closing_time) && (
                                            <option value={doctor.open_day_times[day].closing_time}>
                                              {to12h(doctor.open_day_times[day].closing_time)}
                                            </option>
                                          )}
                                          {HOUR_OPTIONS.map((h) => (
                                            <option key={`close-${day}-${h}`} value={h}>{to12h(h)}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <span className="mx-1">-</span>
                                      <div className="input-group input-group-sm">
                                        <span className="input-group-text"><i className="far fa-clock"></i></span>
                                        <select
                                          className="form-select"
                                          value={doctor.open_day_times?.[day]?.opening_time || ''}
                                          onChange={(e) => handleTimeChange(day, 'opening_time', e.target.value)}
                                        >
                                          <option value="">—</option>
                                          {doctor.open_day_times?.[day]?.opening_time && !HOUR_OPTIONS.includes(doctor.open_day_times[day].opening_time) && (
                                            <option value={doctor.open_day_times[day].opening_time}>
                                              {to12h(doctor.open_day_times[day].opening_time)}
                                            </option>
                                          )}
                                          {HOUR_OPTIONS.map((h) => (
                                            <option key={`open-${day}-${h}`} value={h}>{to12h(h)}</option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <small className="text-muted d-block text-start mt-1">You can leave times empty if not specified.</small>
                          </div>
                        )}
                      </div>

                      {/* Section: Closed Dates */}
                      <div className="mb-0 p-3 border rounded bg-white">
                        <h6 className="mb-3 text-start">
                          <i className="far fa-calendar-times me-2"></i>
                          Closed Dates (e.g., holidays or vacations)
                        </h6>
                        <div className="d-flex gap-2 align-items-center flex-wrap" dir="ltr">
                          <input
                            type="date"
                            className="form-control text-end"
                            style={{ maxWidth: '220px' }}
                            value={newClosedDate}
                            onChange={(e) => setNewClosedDate(e.target.value)}
                          />
                          <button type="button" className="btn btn-primary" onClick={handleAddClosedDate}>
                            <i className="fas fa-plus me-1"></i>
                            Add Closed Date
                          </button>
                        </div>
                        <small className="text-muted d-block text-start mt-1">These dates will appear as unavailable for booking.</small>
                        <div className="mt-3">
                          {closedDates.length === 0 ? (
                            <div className="text-muted text-start">No closed dates</div>
                          ) : (
                            <ul className="list-group">
                              {closedDates.map((item) => (
                                <li key={item.id ?? item.date} className="list-group-item d-flex justify-content-between align-items-center" dir="ltr">
                                  <span>
                                    <i className="far fa-calendar-times me-2 text-danger"></i>
                                    {item.date}
                                  </span>
                                  {item.id ? (
                                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteClosedDate(item.id)}>
                                      <i className="fas fa-trash-alt"></i>
                                    </button>
                                  ) : (
                                    <span className="badge bg-secondary">Saved from Profile</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'social' && (
                    <div className="search-filters-section mb-4">
                      <h5 className="mb-3 text-start">
                        <i className="fas fa-share-alt me-2"></i>
                        Social Media
                      </h5>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label htmlFor="facebook_url" className="form-label text-start d-block">
                            <i className="fa-brands fa-facebook me-2" style={{color: '#1877F2'}}></i>
                            Facebook Link
                          </label>
                          <input
                            type="url"
                            className="form-control"
                            id="facebook_url"
                            name="facebook_url"
                            value={doctor.facebook_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://facebook.com/your-profile"
                            dir="ltr"
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="instagram_url" className="form-label text-start d-block">
                            <i className="fa-brands fa-instagram me-2" style={{color: '#E4405F'}}></i>
                            Instagram Link
                          </label>
                          <input
                            type="url"
                            className="form-control"
                            id="instagram_url"
                            name="instagram_url"
                            value={doctor.instagram_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://instagram.com/your-profile"
                            dir="ltr"
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-4">
                          <label htmlFor="tiktok_url" className="form-label text-start d-block">
                            <i className="fa-brands fa-tiktok me-2" style={{color: '#000000'}}></i>
                            TikTok Link
                          </label>
                          <input
                            type="url"
                            className="form-control"
                            id="tiktok_url"
                            name="tiktok_url"
                            value={doctor.tiktok_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://www.tiktok.com/@yourhandle"
                            dir="ltr"
                          />
                        </div>
                        <div className="col-md-4">
                          <label htmlFor="snapchat_url" className="form-label text-start d-block">
                            <i className="fa-brands fa-snapchat me-2" style={{color: '#FFFC00'}}></i>
                            Snapchat Link
                          </label>
                          <input
                            type="url"
                            className="form-control"
                            id="snapchat_url"
                            name="snapchat_url"
                            value={doctor.snapchat_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://www.snapchat.com/add/yourhandle"
                            dir="ltr"
                          />
                        </div>
                        <div className="col-md-4">
                          <label htmlFor="telegram_url" className="form-label text-start d-block">
                            <i className="fa-brands fa-telegram me-2" style={{color: '#229ED9'}}></i>
                            Telegram Link
                          </label>
                          <input
                            type="url"
                            className="form-control"
                            id="telegram_url"
                            name="telegram_url"
                            value={doctor.telegram_url || ''}
                            onChange={handleInputChange}
                            placeholder="https://t.me/yourchannel_or_username"
                            dir="ltr"
                          />
                        </div>
                      </div>

                      {doctor.username && doctor.consultation_fee !== 9999999 && doctor.consultation_fee !== '9999999' && (
                        <div className="search-filters-section mt-4">
                          <h6 className="mb-3 text-start">
                            <i className="fas fa-link me-2"></i>
                            Profile Link
                          </h6>
                          <div className="profile-url-section p-3 bg-light border rounded">
                            <p className="text-muted mb-3 text-start">
                              You can copy and share this link to display your profile
                            </p>
                            <div
                              className="profile-url-display p-3 mb-3 bg-white border rounded"
                              style={{
                                direction: 'ltr',
                                textAlign: 'left',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                wordBreak: 'break-all',
                                backgroundColor: '#ffffff',
                                borderColor: '#dee2e6'
                              }}
                            >
                              {`${window.location.origin}/doctor/${doctor.username}`}
                            </div>
                            <div className="d-flex gap-2 justify-content-end">
                              <a
                                href={`${window.location.origin}/doctor/${doctor.username}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-info"
                                style={{ borderRadius: '8px', padding: '0.75rem 1.5rem' }}
                              >
                                <i className="fas fa-external-link-alt me-2"></i>
                                View Public Profile
                              </a>
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={copyProfileURL}
                                disabled={copyingQR}
                                style={{ borderRadius: '8px', padding: '0.75rem 1.5rem' }}
                              >
                                {copyingQR ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Copying...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-copy me-2"></i>
                                    Copy Link
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Profile tab removed; content merged into Social tab below */}

                  <div className="d-flex flex-column flex-md-row justify-content-end align-items-stretch gap-2">
                    <button
                      type="submit"
                      className="btn create-patient-btn order-1 order-md-3"
                      disabled={saving}
                      style={{ background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', border: 'none' }}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          Save 
                        </>
                      )}
                    </button>
                  </div>
              </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" dir="rtl">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Confirm Account Deletion
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword('');
                  }}
                  disabled={deleting}
                ></button>
              </div>
              <div className="modal-body text-start">
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-circle me-2"></i>
                  <strong>Warning:</strong> This action cannot be undone!
                </div>
                <p className="mb-3">
                  Your account and all your data will be permanently deleted, including:
                </p>
                <ul className="text-start mb-3">
                  <li>Profile information</li>
                  <li>Patient list and notes</li>
                  <li>Prescriptions and referrals</li>
                  <li>All data associated with your account</li>
                </ul>
                <p className="text-danger fw-bold mb-3">
                  Are you sure you want to permanently delete your account?
                </p>
                <div className="mb-3">
                  <label htmlFor="delete_password" className="form-label text-start d-block">
                    Enter Password to Confirm *
                  </label>
                  <input
                    type="password"
                    className="form-control text-start"
                    id="delete_password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Password"
                    disabled={deleting}
                    dir="ltr"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword('');
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={deleting || !deletePassword}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-alt me-2"></i>
                      Delete Account Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorInformation;
